import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Trophy, Medal, Camera, Users, Swords, Calendar, MapPin } from "lucide-react";
import ShareMenu from "@/components/ShareMenu";
import { fmtDate, eventUrl } from "@/pages/PublicEvents";
import { setMeta, setCanonical, injectLd, clearLd } from "@/lib/seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SITE = "https://www.picklebullpen.com";

export default function EventRecap() {
  const { id } = useParams();
  const [t, setT] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    axios.get(`${API}/public/events/${id.slice(-24)}/recap`).then(({ data }) => {
      setT(data);
      const canonical = `${SITE}${eventUrl(data)}/recap`;
      const champLine = Object.values(data.champions || {}).map((c) => c.champion_name).filter(Boolean).join(", ");
      const title = `${data.name} Results & Champions — Pickleball Tournament Recap · ${fmtDate(data.date, data.end_date)} | Pickle Bullpen`;
      const desc = `Full results from ${data.name} at ${data.location}: champions ${champLine || "crowned"}, medal-match scores, podium finishes and photos. ${data.player_count} players · ${data.match_count} matches played.`;
      document.title = title;
      setMeta("name", "description", desc);
      setMeta("property", "og:title", title);
      setMeta("property", "og:description", desc);
      setMeta("property", "og:url", canonical);
      if (data.image) setMeta("property", "og:image", data.image);
      setCanonical(canonical);
      injectLd("recap", [
        { "@context": "https://schema.org", "@type": "NewsArticle", headline: `${data.name} Results & Champions`,
          datePublished: data.date, dateModified: data.end_date || data.date,
          author: { "@type": "Organization", name: "Pickle Bullpen", url: SITE },
          publisher: { "@type": "Organization", name: "Pickle Bullpen", url: SITE,
            logo: { "@type": "ImageObject", url: `${SITE}/og-image.jpg` } },
          image: data.image || `${SITE}/og-image.jpg`, mainEntityOfPage: canonical, description: desc },
        { "@context": "https://schema.org", "@type": "BreadcrumbList", itemListElement: [
          { "@type": "ListItem", position: 1, name: "Pickle Bullpen", item: SITE },
          { "@type": "ListItem", position: 2, name: "Pickleball Tournaments & Events", item: `${SITE}/events` },
          { "@type": "ListItem", position: 3, name: data.name, item: `${SITE}${eventUrl(data)}` },
          { "@type": "ListItem", position: 4, name: "Results & Champions", item: canonical } ] },
      ]);
    }).catch(() => setMissing(true));
    return () => clearLd("recap");
  }, [id]);

  if (missing) return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex flex-col items-center justify-center gap-4" data-testid="recap-not-found">
      <p className="uppercase tracking-widest text-zinc-500">Recap not available yet — champions are crowned when the tournament finishes</p>
      <Link to="/events" className="text-lime-400 text-sm underline">See all events</Link>
    </div>
  );
  if (!t) return null;
  const champs = Object.entries(t.champions || {});
  const golds = t.medal_matches.filter((m) => m.kind === "final");
  const bronzes = t.medal_matches.filter((m) => m.kind === "bronze");

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white px-6 py-10" data-testid="event-recap-page">
      <div className="max-w-3xl mx-auto">
        <nav className="mb-8 text-xs uppercase tracking-widest text-zinc-500">
          <Link to="/events" className="hover:text-lime-400 transition-colors inline-flex items-center gap-1"><ArrowLeft className="w-3 h-3" /> All Events</Link>
          <span className="mx-2 text-zinc-700">/</span>
          <Link to={eventUrl(t)} data-testid="recap-event-link" className="hover:text-lime-400 transition-colors">{t.name}</Link>
        </nav>
        <p className="font-mono text-amber-400 uppercase tracking-[0.3em] text-xs mb-3">🏆 Official Results & Champions</p>
        <h1 className="font-display font-black uppercase leading-[0.95] text-4xl sm:text-5xl mb-3" data-testid="recap-title">{t.name} — Recap</h1>
        <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-zinc-400 mb-8">
          <span className="inline-flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5 text-lime-400" /> {fmtDate(t.date, t.end_date)}</span>
          <span className="inline-flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-lime-400" /> {t.location}{t.venue ? ` (${t.venue})` : ""}</span>
          <span className="inline-flex items-center gap-1.5"><Users className="w-3.5 h-3.5 text-lime-400" /> {t.player_count} players</span>
          <span className="inline-flex items-center gap-1.5"><Swords className="w-3.5 h-3.5 text-lime-400" /> {t.match_count} matches</span>
        </div>
        {t.image && <img src={t.image} alt={`${t.name} pickleball tournament champions`} className="w-full max-h-80 object-cover border border-zinc-800 mb-8" />}

        {champs.map(([div, c]) => (
          <div key={div} className="mb-6 border border-amber-400/40 bg-amber-400/5 p-5" data-testid={`recap-podium-${div}`}>
            <h2 className="flex items-center gap-2 font-display font-extrabold uppercase text-amber-400 text-lg mb-4">
              <Trophy className="w-4 h-4" /> {div} Podium
            </h2>
            <div className="space-y-2.5">
              {[["🥇", "Champion", c.champion_name, "text-amber-300 font-black text-lg"],
                ["🥈", "Runner-Up", c.runner_up_name, "text-zinc-200 font-bold"],
                ["🥉", "Third Place", c.third_name, "text-orange-300/90 font-bold"]].map(([ico, lbl, name, cls]) => name ? (
                <div key={lbl} className="flex items-center gap-3">
                  <span className="text-2xl">{ico}</span>
                  <div>
                    <div className="text-[9px] uppercase tracking-[0.25em] text-zinc-500">{lbl}</div>
                    <div className={cls}>{name}</div>
                  </div>
                </div>
              ) : null)}
            </div>
          </div>
        ))}

        {(golds.length > 0 || bronzes.length > 0) && (
          <div className="mb-8 border border-zinc-800 bg-white/[0.02] p-5" data-testid="recap-medal-matches">
            <h2 className="flex items-center gap-2 font-display font-extrabold uppercase text-lg mb-4"><Medal className="w-4 h-4 text-lime-400" /> Medal Match Scores</h2>
            <div className="space-y-2 text-sm">
              {[...golds, ...bronzes].map((m, i) => (
                <div key={i} className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-zinc-900 pb-2" data-testid={`recap-medal-${i}`}>
                  <span className={`text-[9px] font-black uppercase tracking-widest px-2 py-0.5 ${m.kind === "final" ? "bg-amber-400 text-black" : "border border-orange-400/50 text-orange-300"}`}>
                    {m.kind === "final" ? "🥇 Gold Match" : "🥉 Bronze Match"}
                  </span>
                  <span className="text-zinc-500 text-[10px] uppercase tracking-widest">{m.division}</span>
                  <span className="font-semibold">{m.winner}</span>
                  <span className="text-[10px] uppercase tracking-widest text-lime-400 font-bold">def.</span>
                  <span className="text-zinc-300">{m.loser}</span>
                  <span className="font-mono text-lime-300">{m.score}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mb-8 border border-zinc-800 bg-white/[0.02] p-5" data-testid="recap-seo-copy">
          <p className="text-sm text-zinc-400 leading-relaxed">
            {t.name} brought {t.player_count} players to {t.location} for {t.match_count} matches of sanctioned pickleball on
            the Pickle Bullpen circuit. Every result fed the players' <strong className="text-zinc-200">PSI ratings</strong> and{" "}
            <strong className="text-zinc-200">PBR ranking points</strong>, and these champions moved one step closer to State and National qualification.
            Want your name on the next recap? Create a free profile and enter the next event.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-4 border-t border-zinc-900 pt-8">
          <Link to="/auth?mode=register" data-testid="recap-join-btn"
            className="inline-flex items-center gap-2 rounded-full bg-lime-400 text-black font-display font-bold uppercase tracking-wider px-8 py-3 hover:bg-lime-300 transition-colors">
            Play the Next One
          </Link>
          {t.album_id && (
            <Link to={`/gallery/${t.album_id}`} data-testid="recap-gallery-link"
              className="inline-flex items-center gap-2 border border-zinc-700 rounded-full px-6 py-3 text-sm font-bold uppercase tracking-wider hover:border-lime-400 hover:text-lime-300 transition-colors">
              <Camera className="w-4 h-4" /> Photo Gallery
            </Link>
          )}
          <div className="border border-zinc-800 px-4 py-2.5">
            <ShareMenu url={`${process.env.REACT_APP_BACKEND_URL}/api/share/events/${t.id}`} image={t.image}
              text={`🏆 ${t.name} results are in! Champions: ${champs.map(([, c]) => c.champion_name).join(", ")} — full recap on Pickle Bullpen`}
              testId="recap-share" label="Share the results" />
          </div>
        </div>

        <div className="mt-10 pt-6 border-t border-zinc-900 text-xs text-zinc-500 flex flex-wrap gap-x-5 gap-y-2" data-testid="recap-links">
          <Link to={eventUrl(t)} className="hover:text-lime-400 transition-colors">Event Details</Link>
          <Link to="/events" className="hover:text-lime-400 transition-colors">All Pickleball Tournaments</Link>
          <Link to="/gallery" className="hover:text-lime-400 transition-colors">Photo Gallery</Link>
          <Link to="/faq" className="hover:text-lime-400 transition-colors">How PSI Ratings Work</Link>
        </div>
      </div>
    </div>
  );
}
